import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useState, useEffect, useRef } from 'react';
import { renderTextWithLinks } from '../utils/textFormat';
import { useNavigate, useParams, Link, useLocation } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { fetchEventById, fetchEventByHandle, searchEvents, reportEvent } from '@/apiEndpoints';
import LikeButton from '@/components/LikeButton';
import EventCommentsSection from '@/components/EventCommentsSection';
import ShareModal from '@/components/ShareModal';
import ReportEventModal, { type ReportEventData } from '@/components/ReportEventModal';
import GetTicketsModal from '@/components/GetTicketsModal';
import moment from 'moment';
import { Input } from '@heroui/input';
import { Button } from '@heroui/button';
import { addToast } from '@heroui/toast';
import EventMap from '@/components/EventMap';
import SeatMapSelector, { type SeatTicketChoice } from '@/components/SeatMapSelector';
import { fetchVenue } from '@/api/venueApi';
import { currencyCode, currencySymbol } from '@/utils/currency';
import EventCard from '@/components/EventCard';
import MobileBottomNav from '@/components/MobileBottomNav';
import { IconArrowUp as ArrowUp, IconBuildingSkyscraper as Building2, IconCalendarEvent as CalendarDays, IconChevronDown as ChevronDown, IconChevronLeft as ChevronLeft, IconChevronRight as ChevronRight, IconGift as Gift, IconInfoCircle as Info, IconMapPin as MapPin, IconMinus as Minus, IconPlus as Plus, IconShare as Share2, IconTicket as Ticket, IconUser as UserRound, IconX as X } from '@tabler/icons-react';
import { wrap, pageBg, glow, card, surface, bodyMuted, btnPrimary, btnAccent, btnGhost, input } from '@/components/ui/design';
// removed cart-context import

// Helper function to get site name from environment
const getSiteName = () => {
    return (import.meta as any).env?.VITE_SITE_NAME || 'EventUp';
};

// Remove the mock events array and helpers from the main event list
// import { Calendar } from '@heroui/calendar';
// import { CalendarDate, isSameDay } from '@internationalized/date';
// const events: any[] = [];
// function fetchEventById(id: string) {
//     return new Promise((resolve, reject) => {
//         setTimeout(() => {
//             const event = events.find(e => e.title && e.title.replace(/\W+/g, '-').toLowerCase() === id);
//             if (event) resolve(event);
//             else reject(new Error('Event not found'));
//         }, 500);
//     });
// }

const sectionHeading = 'text-[28px] sm:text-[36px] font-bold leading-none tracking-[-0.04em] text-grey10 dark:text-white';
const checkoutTitle = 'text-[28px] font-bold leading-none tracking-[-0.04em] text-grey10 dark:text-white';
const checkoutSub = 'mt-1.5 text-[15px] font-medium text-grey40 dark:text-nightMuted';
const infoRow = 'flex min-w-0 items-center gap-3 rounded-[24px] bg-grey99 p-3 pr-4 ring-1 ring-black/[0.04] dark:bg-white/[0.04] dark:ring-white/[0.06]';
const infoIcon = 'flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white text-grey10 ring-1 ring-black/[0.06] dark:bg-white/[0.06] dark:text-white dark:ring-white/10';
const stepper = 'flex h-11 shrink-0 items-center gap-1 rounded-full bg-grey99 p-1 ring-1 ring-black/[0.04] dark:bg-white/[0.06] dark:ring-white/[0.06]';
const stepBtn = 'flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-colors disabled:cursor-not-allowed';
const stepBtnOn = 'bg-grey10 text-white hover:bg-black disabled:opacity-40 dark:bg-white dark:text-nightBg dark:hover:bg-grey90';
const stepBtnOff = 'bg-transparent text-grey60 dark:text-nightMuted';
const commentInputWrapper = 'h-12 rounded-2xl bg-grey99 shadow-none ring-1 ring-black/[0.04] data-[hover=true]:bg-grey99 group-data-[focus=true]:bg-grey99 group-data-[focus=true]:ring-2 group-data-[focus=true]:ring-primary/50 dark:bg-white/[0.06] dark:ring-white/10 dark:data-[hover=true]:bg-white/[0.06] dark:group-data-[focus=true]:bg-white/[0.06] dark:group-data-[focus=true]:ring-nightAccent/60';

// Helper function to extract YouTube video ID from URL
const getYouTubeVideoId = (url: string): string | null => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
};

// Banner Carousel Component
function BannerCarousel({ event }: { event: any }) {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [videoActivated, setVideoActivated] = useState(false);

    // Build carousel items array
    const carouselItems: Array<{ type: 'youtube' | 'image'; src: string }> = [];

    // Add YouTube video as first item if available
    if (event?.youtubeVideoLink) {
        const videoId = getYouTubeVideoId(event.youtubeVideoLink);
        if (videoId) {
            carouselItems.push({
                type: 'youtube',
                src: videoId
            });
        }
    }

    // Add main banner if available
    if (event?.attachments?.banner?.link) {
        carouselItems.push({
            type: 'image',
            src: event.attachments.banner.link
        });
    }

    // Add additional banners if available
    if (event?.attachments?.additionalBanners && Array.isArray(event.attachments.additionalBanners)) {
        event.attachments.additionalBanners.forEach((banner: any) => {
            if (banner?.link) {
                carouselItems.push({
                    type: 'image',
                    src: banner.link
                });
            }
        });
    }

    // If no items, show default banner
    if (carouselItems.length === 0) {
        carouselItems.push({
            type: 'image',
            src: '/bannerevent.png'
        });
    }

    const goToSlide = (index: number) => {
        setCurrentIndex(index);
        setVideoActivated(false);
    };

    const goToPrevious = () => {
        setCurrentIndex((prevIndex) => (prevIndex === 0 ? carouselItems.length - 1 : prevIndex - 1));
        setVideoActivated(false);
    };

    const goToNext = () => {
        setCurrentIndex((prevIndex) => (prevIndex === carouselItems.length - 1 ? 0 : prevIndex + 1));
        setVideoActivated(false);
    };

    const currentItem = carouselItems[currentIndex];

    return (
        <div className="relative isolate w-full h-[240px] sm:h-[360px] md:h-[460px] overflow-hidden overscroll-auto rounded-[28px] md:rounded-[36px] bg-grey10 ring-1 ring-black/[0.06] dark:bg-[#101010] dark:ring-white/10 group">
            {/* Blurred background image */}
            {currentItem.type === 'image' && (
                <img
                    src={currentItem.src}
                    alt="Event Banner Background"
                    className="absolute inset-0 w-full h-full object-cover max-md:hidden scale-125 opacity-70"
                />
            )}

            {/* Main content */}
            <div className="relative z-10 w-full h-full">
                {currentItem.type === 'youtube' ? (
                    videoActivated ? (
                        <iframe
                            className="w-full h-full"
                            src={`https://www.youtube.com/embed/${currentItem.src}?autoplay=1&controls=1&rel=0`}
                            title="YouTube video player"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        />
                    ) : (
                        <button
                            type="button"
                            onClick={() => setVideoActivated(true)}
                            className="relative w-full h-full group/play"
                            aria-label="Play video"
                        >
                            <img
                                src={`https://img.youtube.com/vi/${currentItem.src}/hqdefault.jpg`}
                                alt="Video thumbnail"
                                className="w-full h-full object-cover"
                            />
                            <span className="absolute inset-0 flex items-center justify-center bg-black/25 group-hover/play:bg-black/40 transition-colors">
                                <span className="w-20 h-20 rounded-full bg-white flex items-center justify-center shadow-2xl transition-transform duration-300 group-hover/play:scale-110">
                                    <svg width="28" height="28" viewBox="0 0 24 24" fill="#008C95"><path d="M8 5v14l11-7z" /></svg>
                                </span>
                            </span>
                        </button>
                    )
                ) : (
                    <img
                        src={currentItem.src}
                        alt="Event Banner"
                        className="w-full h-full object-contain bg-transparent object-center"
                    />
                )}
            </div>

            {currentItem.type === 'image' && (
                <div className="pointer-events-none absolute inset-0 z-10 bg-gradient-to-t from-black/60 via-black/5 to-transparent" />
            )}

            {/* Navigation arrows - only show if more than one item */}
            {carouselItems.length > 1 && (
                <>
                    <button
                        onClick={goToPrevious}
                        className="absolute left-4 top-1/2 -translate-y-1/2 z-20 flex h-11 w-11 items-center justify-center rounded-full bg-black/40 text-white transition-all hover:bg-white/25 md:opacity-0 md:group-hover:opacity-100"
                        aria-label="Previous slide"
                    >
                        <ChevronLeft className="h-5 w-5" />
                    </button>
                    <button
                        onClick={goToNext}
                        className="absolute right-4 top-1/2 -translate-y-1/2 z-20 flex h-11 w-11 items-center justify-center rounded-full bg-black/40 text-white transition-all hover:bg-white/25 md:opacity-0 md:group-hover:opacity-100"
                        aria-label="Next slide"
                    >
                        <ChevronRight className="h-5 w-5" />
                    </button>
                </>
            )}

            {/* Dots indicator - only show if more than one item */}
            {carouselItems.length > 1 && (
                <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2 rounded-full bg-black/50 px-3 py-2">
                    {carouselItems.map((_, index) => (
                        <button
                            key={index}
                            onClick={() => goToSlide(index)}
                            className={`h-2 rounded-full transition-all ${index === currentIndex ? 'bg-white w-7' : 'w-2 bg-white/50 hover:bg-white/75'
                                }`}
                            aria-label={`Go to slide ${index + 1}`}
                        />
                    ))}
                </div>
            )}

        </div>
    );
}

// Loading skeleton — mirrors the real page layout so there's no jump/reflow once data arrives.
function EventDetailsSkeleton() {
    const block = "animate-pulse bg-grey99 dark:bg-white/[0.06] rounded-full";
    return (
        <div className={`min-h-screen flex flex-col ${pageBg}`}>
            <Header />
            <div className={`${wrap} pt-6`}>
                <div className={`${block} h-4 w-56`} />
            </div>
            <div className={`${wrap} pt-4`}>
                <div className="w-full h-[240px] sm:h-[360px] md:h-[460px] animate-pulse rounded-[28px] md:rounded-[36px] bg-grey99 dark:bg-white/[0.06]" />
            </div>
            <div className={`${wrap} pt-8 pb-32 md:pb-16 flex flex-col gap-6`}>
                <div className="flex flex-col md:flex-row gap-10 lg:gap-16">
                    <div className="flex-1 min-w-0 flex flex-col gap-8">
                        <div className="flex items-start gap-4">
                            <div className="h-[72px] w-14 shrink-0 animate-pulse rounded-2xl bg-grey99 dark:bg-white/[0.06]" />
                            <div className="flex-1 flex flex-col gap-3 pt-1">
                                <div className={`${block} h-9 w-full max-w-[460px]`} />
                                <div className={`${block} h-9 w-2/3 max-w-[300px]`} />
                            </div>
                        </div>
                        <div className="grid gap-3 sm:grid-cols-2">
                            {[0, 1, 2].map((i) => (
                                <div key={i} className="h-[76px] animate-pulse rounded-[24px] bg-grey99 dark:bg-white/[0.06]" />
                            ))}
                        </div>
                        <div className="flex items-center gap-3 flex-wrap">
                            <div className={`${block} h-12 w-44`} />
                            <div className={`${block} h-12 w-32`} />
                            <div className={`${block} h-12 w-12`} />
                        </div>
                        <div className="flex flex-col gap-3">
                            <div className={`${block} h-8 w-32`} />
                            {[0, 1, 2].map((i) => (
                                <div key={i} className={`${card} p-5 flex items-center justify-between gap-4`}>
                                    <div className="flex-1 flex flex-col gap-2">
                                        <div className={`${block} h-4 w-32`} />
                                        <div className={`${block} h-4 w-20`} />
                                    </div>
                                    <div className={`${block} h-11 w-[124px]`} />
                                </div>
                            ))}
                        </div>
                        <div className="flex flex-col gap-3">
                            <div className={`${block} h-8 w-24`} />
                            <div className={`${block} h-4 w-full`} />
                            <div className={`${block} h-4 w-full`} />
                            <div className={`${block} h-4 w-2/3`} />
                        </div>
                    </div>
                    <div className="w-full md:w-[380px] lg:w-[400px] flex-shrink-0">
                        <div className={`hidden md:flex flex-col gap-4 p-6 ${card}`}>
                            <div className={`${block} h-8 w-40`} />
                            <div className={`${block} h-12 w-full`} />
                            <div className={`${block} h-12 w-full`} />
                        </div>
                    </div>
                </div>
            </div>
            <Footer hasMobileNav />
            <MobileBottomNav />
        </div>
    );
}

// Safari can't parse "YYYY-MM-DD HH:mm"; normalize to "YYYY-MM-DDTHH:mm" (local time).
function parseEventDate(value: any): Date | null {
    if (!value) return null;
    const normalized = typeof value === 'string' && /^\d{4}-\d{2}-\d{2} \d{1,2}:\d{2}/.test(value) ? value.replace(' ', 'T') : value;
    const d = new Date(normalized);
    return isNaN(d.getTime()) ? null : d;
}

export default function EventDetails() {
    const navigate = useNavigate();
    const location = useLocation();
    const { id, handle } = useParams();
    const sgdRef = useRef<string | null>(null);

    // Capture sgd once, send it to backend, then remove it from the URL
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const sgd = params.get('sgd');
        if (!sgd) return;

        if (!sgdRef.current) {
            sgdRef.current = sgd;
        }

        params.delete('sgd');
        const newSearch = params.toString();
        navigate(
            { pathname: location.pathname, search: newSearch ? `?${newSearch}` : '', hash: location.hash },
            { replace: true }
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Helper function to get base URL
    const getBaseUrl = () => {
        if (typeof window !== 'undefined') {
            return window.location.origin;
        }
        return '';
    };
    const [ticketCounts, setTicketCounts] = useState<{ [key: string]: number }>({});
    const [promoCode, setPromoCode] = useState('');
    const [showPromoInput, setShowPromoInput] = useState(false);
    const [showFullAbout, setShowFullAbout] = useState(false);
    const [selectedTime, setSelectedTime] = useState<string | null>(null);
    const [selectedTimeEnd, setSelectedTimeEnd] = useState<string | null>(null);

    // Seat selection state
    const [selectedSeats, setSelectedSeats] = useState<string[]>([]);
    const [seatSheetOpen, setSeatSheetOpen] = useState(false);
    const [unresolvedSeats, setUnresolvedSeats] = useState<string[]>([]);
    const [seatChoices, setSeatChoices] = useState<SeatTicketChoice[]>([]);
    const [openSeatId, setOpenSeatId] = useState<string | null>(null);
    const [seatChoiceAttempted, setSeatChoiceAttempted] = useState(false);
    useEffect(() => { if (unresolvedSeats.length === 0) setSeatChoiceAttempted(false); }, [unresolvedSeats]);
    const [seatTicketChoice, setSeatTicketChoice] = useState<Record<string, string>>({});
    const handleSeatTicketChoice = (seatId: string, ticketId: string | null) => {
        if (ticketId !== null) setOpenSeatId(null);
        setSeatTicketChoice(prev => {
            if (ticketId === null) {
                if (!(seatId in prev)) return prev;
                const { [seatId]: _removed, ...rest } = prev;
                return rest;
            }
            return { ...prev, [seatId]: ticketId };
        });
    };
    const [seatAssignments, setSeatAssignments] = useState<Array<{
        seatUuid: string;
        ticketId: string;
        status: string;
        price?: number;
    }>>([]);

    // Handle like changes
    const handleLikeChange = (likes: number, isLiked: boolean) => {
        console.log(`Event like status changed: ${likes} likes, isLiked: ${isLiked}`);
        // You can add additional logic here like updating local state, showing notifications, etc.
    };

    // Seat selection handlers
    const handleSeatSelect = (seatUuid: string, seatNumber: string) => {
        setSelectedSeats(prev => [...prev, seatUuid]);
        console.log(`Selected seat ${seatNumber} (${seatUuid})`);
    };

    const handleSeatDeselect = (seatUuid: string) => {
        setSelectedSeats(prev => prev.filter(id => id !== seatUuid));
        setSeatTicketChoice(prev => {
            if (!(seatUuid in prev)) return prev;
            const { [seatUuid]: _removed, ...rest } = prev;
            return rest;
        });
        console.log(`Deselected seat ${seatUuid}`);
    };

    // Handle seat assignments loaded from SeatMapSelector
    const handleSeatAssignmentsLoaded = (assignments: Array<{
        seatUuid: string;
        ticketId: string;
        status: string;
        price?: number;
    }>) => {
        setSeatAssignments(assignments);
    };

    // FAQ accordion state
    const [expandedFaqs, setExpandedFaqs] = useState<{ [key: number]: boolean }>({});

    // Toggle FAQ accordion
    const toggleFaq = (index: number) => {
        setExpandedFaqs(prev => ({
            ...prev,
            [index]: !prev[index]
        }));
    };

    // Determine if we're using handle or ID
    const eventIdentifier = handle || id;
    const isHandle = !!handle;

    // Fetch event details
    const { data: event, isLoading, isError } = useQuery<any>({
        queryKey: ['event', eventIdentifier, isHandle ? 'handle' : 'id'],
        queryFn: () => isHandle
            ? fetchEventByHandle(handle as string, sgdRef.current || undefined)
            : fetchEventById(id as string, sgdRef.current || undefined),
        enabled: !!eventIdentifier,
    });

    const { data: venueResponse } = useQuery({
        queryKey: ['venue', event?.data?.venueId],
        queryFn: () => {
            if (!event?.data?.venueId) {
                throw new Error('Venue ID is required');
            }
            return fetchVenue(event.data.venueId);
        },
        enabled: !!event?.data?.venueId,
    });



    // Auto-select the first occurrence that hasn't ended yet (no manual date picker).
    // An occurrence that already started but is still running stays bookable.
    useEffect(() => {
        const times = event?.data?.times;
        if (!Array.isArray(times) || times.length === 0) {
            setSelectedTime(null);
            setSelectedTimeEnd(null);
            return;
        }
        const now = new Date();
        const occurrences: { start: Date; end: Date }[] = [];
        times.forEach((range: any) => {
            const start = parseEventDate(range.startDate);
            const end = parseEventDate(range.endDate) || start;
            if (!start || !end) return;
            for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
                const dayEnd = new Date(d);
                dayEnd.setHours(end.getHours(), end.getMinutes(), end.getSeconds(), 0);
                if (dayEnd < d) dayEnd.setDate(dayEnd.getDate() + 1);
                occurrences.push({ start: new Date(d), end: dayEnd > end ? new Date(end) : dayEnd });
            }
        });
        const next = occurrences
            .filter(o => o.end >= now)
            .sort((a, b) => a.start.getTime() - b.start.getTime())[0];
        setSelectedTime(next ? next.start.toISOString() : null);
        setSelectedTimeEnd(next ? next.end.toISOString() : null);
    }, [event?.data?.times]);

    // Handle scroll to show/hide back to top button
    useEffect(() => {
        const handleScroll = () => {
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

            // Show back to top button when user scrolls down more than 300px
            const shouldShowBackToTop = scrollTop > 300;
            setShowBackToTop(shouldShowBackToTop);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Tickets are now included in event data
    const tickets = event?.data?.tickets || [];


    // Fetch related events (by category or organizer)
    const { data: relatedEventsData = { data: [] } } = useQuery({
        queryKey: ['relatedEvents', event?.data?.category, event?.data?.userId],
        queryFn: () => {
            const startDate = new Date().toISOString().split('T')[0];
            if (event?.data?.category) {
                return searchEvents({ category: event?.data?.category, limit: 4, startDate });
            } else if (event?.data?.userId) {
                return searchEvents({ organizerId: event?.data?.userId, limit: 4, startDate });
            }
            return { data: [] };
        },
        enabled: !!(event?.data?.category || event?.data?.userId),
    });
    const relatedEvents = (relatedEventsData?.data || []).filter((e: any) => e._id !== event?.data?._id).slice(0, 3);

    // Report event mutation
    const reportEventMutation = useMutation({
        mutationFn: async (reportData: ReportEventData) => {
            if (!event?.data?._id) {
                throw new Error('Event ID not found');
            }
            return reportEvent(event.data._id, reportData.reason, reportData.description);
        },
        onSuccess: () => {
            setShowReportModal(false);
            addToast({
                title: 'Report Submitted',
                description: 'Your report has been submitted successfully. Thank you for helping us maintain a safe community.',
                color: 'success',
            });
        },
        onError: (error: any) => {
            setShowReportModal(false);
            console.error('Failed to report event:', error);
            addToast({
                title: 'Report Failed',
                description: error?.response?.data?.message || 'Failed to submit your report. Please try again later.',
                color: 'danger'
            });
        },
    });

    // Remove CalendarDate helper and any linter errors

    // Helper to format date as 'YYYY/MM/DD HH:mm'
    function formatSelectedDate(dateString: string | null) {
        console.log('ddd', dateString);
        if (!dateString) return '';
        const d = new Date(dateString);
        const pad = (n: number) => n.toString().padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    }

    // Min ticket price shown above the Get Tickets button
    const ticketPrices = tickets.map((t: any) => Number(t.price)).filter((p: number) => !isNaN(p));
    const minTicketPrice = ticketPrices.length > 0 ? Math.min(...ticketPrices) : null;
    const cur = currencySymbol(event?.data?.currency);
    const curCode = currencyCode(event?.data?.currency);

    // Compute if at least one ticket is selected
    const hasSelectedTickets = Object.values(ticketCounts).some(count => count > 0);
    const totalTicketCount = Object.values(ticketCounts).reduce((sum, count) => sum + count, 0);
    const hasSelectedSeats = event?.data?.venueId && selectedSeats.length > 0;
    const hasAnySelection = hasSelectedTickets || hasSelectedSeats;
    const [showCheckoutWarning, setShowCheckoutWarning] = useState(false);
    const [, setValidationErrors] = useState<{ date?: boolean; tickets?: boolean }>({});
    const [showBackToTop, setShowBackToTop] = useState(false);
    const [showShareModal, setShowShareModal] = useState(false);
    const [showReportModal, setShowReportModal] = useState(false);
    const [showGetTicketsModal, setShowGetTicketsModal] = useState(false);

    // Check if selected time is in the past
    const isSelectedTimePassed = selectedTime ? new Date(selectedTimeEnd || selectedTime) < new Date() : false;

    // Event status logic based on dates
    const getEventStatus = () => {
        if (!event?.data?.times || event.data.times.length === 0) return 'unknown';

        const now = new Date();
        const allEndDates = event.data.times.map((time: any) => parseEventDate(time.endDate) || new Date(NaN));
        const allStartDates = event.data.times.map((time: any) => parseEventDate(time.startDate) || new Date(NaN));

        // Check if all end dates have passed
        const allEnded = allEndDates.every((endDate: Date) => endDate < now);
        if (allEnded) return 'passed';

        // Check if no start dates have been reached yet
        const anyStarted = allStartDates.some((startDate: Date) => startDate <= now);
        if (!anyStarted) return 'new';

        return 'ongoing';
    };

    const eventStatus = getEventStatus();
    const isEventPassed = eventStatus === 'passed';
    const showMobileCheckoutBar = isEventPassed || !!event?.data?.venueId || totalTicketCount > 0;

    // Calculate total price
    const regularTicketPrice = (tickets || []).reduce((sum: number, ticket: any, i: number) => {
        const ticketId = ticket._id || i;
        const count = ticketCounts[ticketId] ?? 0;
        return sum + (count * (Number(ticket.price) || 0));
    }, 0);

    // Calculate seat selection total price if venueId exists
    const seatSelectionPrice = event?.data?.venueId && selectedSeats.length > 0
        ? selectedSeats.reduce((total, seatUuid) => {
            const seatAssignment = seatAssignments?.find(assignment => assignment.seatUuid === seatUuid);
            const ticketInfo = seatAssignment ? tickets?.find((ticket: any) => ticket._id === seatAssignment.ticketId) : null;
            const price = typeof seatAssignment?.price === 'number' ? seatAssignment.price : ticketInfo?.price ? Number(ticketInfo.price) : 0;
            return total + (isNaN(price) ? 0 : price);
        }, 0)
        : 0;

    const totalPrice = Number(regularTicketPrice) + Number(seatSelectionPrice);


    // Remove the useEffect that preselects tickets from the cart

    if (isLoading) {
        return <EventDetailsSkeleton />;
    }

    // Handle event not found or error
    if (isError || !event?.data) {
        return (
            <div className={`min-h-screen flex flex-col ${pageBg}`}>
                <Header />
                <div className={`${wrap} flex-1 flex items-center justify-center py-16`}>
                    <div className="relative isolate flex w-full flex-col items-center rounded-[36px] bg-grey99 px-6 py-20 text-center ring-1 ring-black/[0.04] dark:bg-white/[0.04] dark:ring-white/[0.06]">
                        <div className="text-6xl mb-6">🔍</div>
                        <h1 className="text-[clamp(32px,5vw,56px)] font-bold leading-[0.95] tracking-[-0.045em] text-grey10 dark:text-white mb-4">Event Not Found</h1>
                        <p className={`${bodyMuted} max-w-md mb-8`}>The event you're looking for doesn't exist or may have been removed.</p>
                        <button
                            onClick={() => window.history.back()}
                            className={btnPrimary}
                        >
                            Go Back
                        </button>
                    </div>
                </div>
                <Footer hasMobileNav />
                <MobileBottomNav />
            </div>
        );
    }


    // Related events: up to 4 others, excluding the current event
    // const relatedEvents: any[] = []; // No mock data, so no related events


    // Helper to convert Date to CalendarDate
    // function toCalendarDate(date: Date) {
    //     return new CalendarDate(date.getFullYear(), date.getMonth() + 1, date.getDate());
    // }

    const handleApplyPromo = () => {
        if (!promoCode.trim()) return;
        setValidationErrors({});
        if (!selectedTime) {
            setValidationErrors({ date: true });
            setShowCheckoutWarning(true);
            document.getElementById('date-time')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
        }
        setShowCheckoutWarning(false);
        setShowGetTicketsModal(true);
    };

    const getSeatDetails = (seatUuid: string) => {
        let details = { zone: '', row: '', seat: '', zoneCode: '' };
        venueResponse?.data?.seatMap?.zones?.forEach((z: any) => {
            z.rows?.forEach((r: any) => {
                r.seats?.forEach((s: any) => {
                    const uid = s.uuid || s.seat_guid || `${z.name}-${r.row_number}-${s.seat_number}`;
                    if (uid === seatUuid) details = { zone: z.name, row: r.row_number, seat: s.seat_number, zoneCode: z.code || '' };
                });
            });
        });
        return details;
    };

    const handleSeatContinue = () => {
        if (unresolvedSeats.length > 0) {
            const firstUnresolved = unresolvedSeats[0];
            setSeatChoiceAttempted(true);
            setOpenSeatId(firstUnresolved);
            window.setTimeout(() => {
                const target = document.querySelector<HTMLInputElement>(`input[name="ticket-${CSS.escape(firstUnresolved)}"]`);
                target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                target?.focus({ preventScroll: true });
            }, 50);
            return;
        }
        setValidationErrors({});
        const errors: { date?: boolean; tickets?: boolean } = {};
        if (!selectedTime) errors.date = true;
        if (!hasAnySelection) errors.tickets = true;
        if (Object.keys(errors).length > 0) {
            setValidationErrors(errors);
            setShowCheckoutWarning(true);
            if (errors.date) document.getElementById('date-time')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            else document.getElementById('tickets')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
        }
        setShowCheckoutWarning(false);
        setShowGetTicketsModal(true);
    };

    const modalSeats = event?.data?.venueId
        ? selectedSeats.flatMap((seatUuid) => {
            const seatAssignment = seatAssignments?.find(x => x.seatUuid === seatUuid);
            if (!seatAssignment) return [];
            const d = getSeatDetails(seatUuid);
            return [{
                seatUuid,
                ticketId: seatAssignment.ticketId,
                label: d.zone ? `${d.zoneCode || d.zone} · Row ${d.row} · Seat ${d.seat}` : 'Seat',
                price: typeof seatAssignment.price === 'number' ? seatAssignment.price : undefined,
            }];
        })
        : [];

    const seatPriceLabel = (price: number) => (price > 0 ? `${cur}${price % 1 ? price.toFixed(2) : price}` : 'Free');
    const seatsResolved = unresolvedSeats.length === 0;
    const totalLabel = seatsResolved ? `${cur}${(totalPrice || 0).toFixed(2)}` : '\u2014';

    const renderSeatCard = (seatUuid: string) => {
        const d = getSeatDetails(seatUuid);
        const choice = seatChoices.find(c => c.seatId === seatUuid);
        const assignment = seatAssignments?.find(a => a.seatUuid === seatUuid);
        const ticketInfo = assignment ? tickets?.find((t: any) => t._id === assignment.ticketId) : null;
        const seatName = d.zone ? `${d.zoneCode || d.zone} \u00b7 Row ${d.row} \u00b7 Seat ${d.seat}` : (choice?.label || 'Seat');
        const heading = choice?.tier ? `${seatName} \u00b7 ${choice.tier}` : seatName;
        const chosen = choice?.options.find(o => o.ticketId === choice.chosenTicketId);
        const missing = !!choice && !chosen;
        const expanded = !!choice && (openSeatId === seatUuid || (missing && seatUuid === unresolvedSeats[0]));
        const showError = missing && seatChoiceAttempted;
        const headingId = `seat-name-${seatUuid}`;
        const errorId = `seat-err-${seatUuid}`;
        let summary: string;
        if (chosen) summary = `${chosen.name} \u00b7 ${seatPriceLabel(chosen.price)}`;
        else if (choice) summary = 'Select ticket type';
        else if (ticketInfo) summary = `${ticketInfo.name} \u00b7 ${seatPriceLabel(typeof assignment?.price === 'number' ? assignment.price : Number(ticketInfo.price))}`;
        else summary = '\u2014';
        return (
            <div key={seatUuid} className={`shrink-0 rounded-2xl bg-grey99 px-4 py-3 dark:bg-white/[0.06] ${showError ? 'ring-1 ring-red-500' : ''}`}>
                <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                        <div id={headingId} title={heading} className="truncate text-[15px] font-semibold text-grey10 dark:text-white">{heading}</div>
                        <div className={`truncate text-[13px] ${missing ? 'font-medium text-grey10 dark:text-white' : 'text-grey40 dark:text-nightMuted'}`}>
                            {summary}
                            {choice && chosen && !expanded && (
                                <button type="button" onClick={() => setOpenSeatId(seatUuid)} className="ml-2 font-semibold text-primary underline-offset-2 hover:underline dark:text-nightAccent" aria-label={`Change ticket type for ${seatName}`}>Change</button>
                            )}
                            {missing && !expanded && (
                                <button type="button" onClick={() => setOpenSeatId(seatUuid)} className="ml-2 font-semibold text-primary underline-offset-2 hover:underline dark:text-nightAccent" aria-label={`Choose ticket type for ${seatName}`}>Choose</button>
                            )}
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={() => handleSeatDeselect(seatUuid)}
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-grey30 ring-1 ring-black/[0.06] transition-colors hover:bg-white dark:text-nightTextSecondary dark:ring-white/10 dark:hover:bg-white/10"
                        aria-label={`Remove ${seatName}`}
                    >
                        <Minus className="h-4 w-4" />
                    </button>
                </div>
                {expanded && choice && (
                    <div role="radiogroup" aria-labelledby={headingId} aria-describedby={showError ? errorId : undefined} className="mt-2 flex flex-col gap-1">
                        {choice.options.map(option => {
                            const active = choice.chosenTicketId === option.ticketId;
                            return (
                                <label
                                    key={option.ticketId}
                                    className={`flex min-h-[36px] cursor-pointer items-center justify-between gap-3 rounded-lg px-3 py-1.5 text-[13px] transition-colors focus-within:ring-2 focus-within:ring-primary ${active ? 'bg-grey10 text-white dark:bg-white dark:text-nightBg' : 'bg-white text-grey10 hover:bg-grey95 dark:bg-white/[0.06] dark:text-white'}`}
                                >
                                    <span className="flex min-w-0 items-center gap-2">
                                        <input
                                            type="radio"
                                            name={`ticket-${choice.seatId}`}
                                            checked={active}
                                            onChange={() => handleSeatTicketChoice(choice.seatId, option.ticketId)}
                                            className="h-4 w-4 shrink-0 accent-primary"
                                        />
                                        <span className="truncate font-medium">{option.name}</span>
                                    </span>
                                    <span className="shrink-0 font-semibold">{seatPriceLabel(option.price)}</span>
                                </label>
                            );
                        })}
                    </div>
                )}
                {showError && <div id={errorId} role="alert" className="mt-1.5 text-[12px] font-medium text-red-600 dark:text-red-400">Required: pick a ticket type for this seat</div>}
            </div>
        );
    };

    const seatListHeader = (
        <div className="flex items-center justify-between gap-3 text-[12px] text-grey40 dark:text-nightMuted">
            <span>{unresolvedSeats.length > 0 ? `${unresolvedSeats.length} of ${selectedSeats.length} need a ticket type` : `${selectedSeats.length} ${selectedSeats.length === 1 ? 'seat' : 'seats'}`}</span>
            <button type="button" onClick={() => selectedSeats.forEach(handleSeatDeselect)} className="font-semibold text-grey30 hover:text-red-600 dark:text-nightTextSecondary dark:hover:text-red-400">Clear all</button>
        </div>
    );

    const seatCheckout = (
        <>
            <div>
                {selectedSeats.length > 0 ? (
                    <>
                        <div className={checkoutTitle}>x{selectedSeats.length}</div>
                        <div className={checkoutSub}>Selected Seats</div>
                    </>
                ) : (
                    <>
                        <div className={checkoutTitle}>Select Seats</div>
                        {minTicketPrice !== null && (
                            <div className={checkoutSub}>{minTicketPrice === 0 ? 'Free' : `From ${cur}${minTicketPrice}`}</div>
                        )}
                    </>
                )}
            </div>
            {selectedSeats.length > 0 && (
                <div className="flex flex-col gap-2">
                    {seatListHeader}
                    {selectedSeats.map(renderSeatCard)}
                </div>
            )}
            {selectedSeats.length > 0 && (
                <div className="w-full border-t border-black/[0.06] pt-4 dark:border-white/10">
                    <div className="flex items-center justify-between text-[22px] font-bold tracking-[-0.03em] text-grey10 dark:text-white leading-8">
                        <span>Total:</span>
                        <span>{totalLabel}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-[13px] text-grey40 dark:text-nightMuted">
                        <span>Includes Fees</span>
                        <Info className="h-4 w-4" />
                    </div>
                </div>
            )}
            <div className="sticky bottom-0 -mx-6 -mb-6 w-[calc(100%+3rem)] bg-white px-6 pb-6 pt-3 dark:bg-nightSurface">
                {isSelectedTimePassed ? (
                    <div className="flex h-12 w-full items-center justify-center rounded-full bg-grey99 text-sm font-semibold text-grey40 dark:bg-white/[0.06] dark:text-nightMuted">Selected Date Has Passed</div>
                ) : (
                    <Button
                        className="no-global-button-color w-full h-12 rounded-full bg-grey10 text-white text-[15px] font-semibold capitalize transition-colors hover:bg-black dark:bg-white dark:text-nightBg dark:hover:bg-grey90 disabled:opacity-60"
                        onClick={handleSeatContinue}
                    >
                        Continue
                    </Button>
                )}
                {showCheckoutWarning && !isSelectedTimePassed && (
                    <div className="text-red-500 dark:text-red-400 text-xs mt-2 text-center">
                        {!selectedTime && !hasAnySelection && "Please select a date/time and at least one seat."}
                        {!selectedTime && hasAnySelection && "Please select a date/time."}
                        {selectedTime && !hasAnySelection && "Please select at least one seat."}
                    </div>
                )}
            </div>
        </>
    );

    const mobileSeatCheckout = (
        <div className="flex flex-col min-h-0 max-h-[min(70dvh,560px)]">
            {selectedSeats.length > 0 ? (
                <button
                    type="button"
                    onClick={() => setSeatSheetOpen(o => !o)}
                    aria-expanded={seatSheetOpen}
                    className="shrink-0 flex items-center gap-3 px-5 pt-2 pb-3 text-left"
                >
                    <div className="min-w-0 flex-1">
                        <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-grey40 dark:text-nightMuted">{selectedSeats.length} {selectedSeats.length === 1 ? 'seat' : 'seats'} selected</div>
                        <div className="text-[22px] font-bold tracking-[-0.03em] text-grey10 dark:text-white leading-7">{totalLabel} <span className="text-[12px] font-medium tracking-normal text-grey40 dark:text-nightMuted">incl. fees</span></div>
                    </div>
                    <span className="flex h-9 items-center gap-1 rounded-full bg-grey99 px-3 text-[13px] font-semibold text-grey10 dark:bg-white/[0.06] dark:text-white">
                        {seatSheetOpen ? 'Hide' : 'Details'}
                        <ChevronDown className={`h-4 w-4 transition-transform ${seatSheetOpen ? '' : 'rotate-180'}`} />
                    </span>
                </button>
            ) : (
                <div className="shrink-0 px-5 pt-2 pb-3">
                    <div className="text-[18px] font-bold tracking-[-0.02em] text-grey10 dark:text-white">Select seats on the map</div>
                    {minTicketPrice !== null && (
                        <div className="text-[13px] text-grey40 dark:text-nightMuted">{minTicketPrice === 0 ? 'Free' : `From ${cur}${minTicketPrice}`}</div>
                    )}
                </div>
            )}
            {seatSheetOpen && selectedSeats.length > 0 && (
                <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain border-t border-black/[0.06] dark:border-white/10 px-5 py-3 flex flex-col gap-2">
                    {seatListHeader}
                    {selectedSeats.map(renderSeatCard)}
                </div>
            )}
            <div className="shrink-0 px-5 pt-3 pb-3 border-t border-black/[0.06] dark:border-white/10">
                {isSelectedTimePassed ? (
                    <div className="flex h-12 w-full items-center justify-center rounded-full bg-grey99 text-sm font-semibold text-grey40 dark:bg-white/[0.06] dark:text-nightMuted">Selected Date Has Passed</div>
                ) : (
                    <Button
                        className="no-global-button-color w-full h-12 rounded-full bg-grey10 text-white text-[15px] font-semibold transition-colors hover:bg-black dark:bg-white dark:text-nightBg dark:hover:bg-grey90 disabled:opacity-60"
                        onClick={handleSeatContinue}
                    >
                        {selectedSeats.length > 0 ? `Continue · ${selectedSeats.length} ${selectedSeats.length === 1 ? 'seat' : 'seats'}` : 'Continue'}
                    </Button>
                )}
                {showCheckoutWarning && !isSelectedTimePassed && (
                    <div className="text-red-500 dark:text-red-400 text-xs mt-2 text-center">
                        {!selectedTime && !hasAnySelection && "Please select a date/time and at least one seat."}
                        {!selectedTime && hasAnySelection && "Please select a date/time."}
                        {selectedTime && !hasAnySelection && "Please select at least one seat."}
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <div className={`min-h-screen flex flex-col ${pageBg}`} style={{ overscrollBehavior: 'none' }}>
            <Helmet>
                <title>{event?.data?.title ? `${event.data.title} - ${getSiteName()}` : `Event - ${getSiteName()}`}</title>
                <meta name="description" content={event?.data?.description || `Join us for ${event?.data?.title || 'this amazing event'} on ${getSiteName()}.`} />
                <meta name="keywords" content={`${event?.data?.title}, event, ${event?.data?.location?.address}, ${event?.data?.organizer?.orgName}, ${getSiteName()}`} />

                {/* Open Graph / Facebook */}
                <meta property="og:type" content="event" />
                <meta property="og:title" content={event?.data?.title ? `${event.data.title} - ${getSiteName()}` : `Event - ${getSiteName()}`} />
                <meta property="og:description" content={event?.data?.description || `Join us for ${event?.data?.title || 'this amazing event'} on ${getSiteName()}.`} />
                <meta property="og:image" content={event?.data?.attachments?.banner?.link || '/bannerevent.png'} />
                <meta property="og:image:width" content="1200" />
                <meta property="og:image:height" content="630" />
                <meta property="og:url" content={window.location.href} />
                <meta property="og:site_name" content={getSiteName()} />

                {/* Event-specific Open Graph tags */}
                {event?.data?.times?.[0]?.startDate && (
                    <meta property="event:start_time" content={new Date(event.data.times[0].startDate).toISOString()} />
                )}
                {event?.data?.times?.[0]?.endDate && (
                    <meta property="event:end_time" content={new Date(event.data.times[0].endDate).toISOString()} />
                )}
                {event?.data?.location?.address && (
                    <meta property="event:location" content={event.data.location.address} />
                )}

                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={event?.data?.title ? `${event.data.title} - ${getSiteName()}` : `Event - ${getSiteName()}`} />
                <meta name="twitter:description" content={event?.data?.description || `Join us for ${event?.data?.title || 'this amazing event'} on ${getSiteName()}.`} />
                <meta name="twitter:image" content={event?.data?.attachments?.banner?.link || '/bannerevent.png'} />

                {/* Additional meta tags */}
                <meta name="robots" content="index, follow" />
                <meta name="author" content={event?.data?.organizer?.orgName || getSiteName()} />
                <link rel="canonical" href={window.location.href} />

                {/* Event structured data for search engines */}
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "Event",
                        "name": event?.data?.title || "Event",
                        "description": event?.data?.description || `Join us for ${event?.data?.title || 'this amazing event'}`,
                        "image": event?.data?.attachments?.banner?.link || '/bannerevent.png',
                        "startDate": event?.data?.times?.[0]?.startDate,
                        "endDate": event?.data?.times?.[0]?.endDate,
                        "location": event?.data?.location?.address ? {
                            "@type": "Place",
                            "name": event.data.location.address,
                            "address": event.data.location.address
                        } : undefined,
                        "organizer": event?.data?.organizer?.orgName ? {
                            "@type": "Organization",
                            "name": event.data.organizer.orgName
                        } : undefined,
                        "offers": event?.data?.tickets?.length > 0 ? {
                            "@type": "Offer",
                            "availability": "https://schema.org/InStock",
                            "price": event.data.tickets[0]?.price || "0",
                            "priceCurrency": "USD"
                        } : undefined
                    })}
                </script>
            </Helmet>
            <style>{`
                .calendar-passed-date {
                    background-color: #fef2f2 !important;
                    color: #dc2626 !important;
                    border: 1px solid #fecaca !important;
                }
                .calendar-passed-date:hover {
                    background-color: #fef2f2 !important;
                    color: #dc2626 !important;
                }
            `}</style>
            <Header />
            <section className="relative isolate">
                <div className={glow} />
                <div className={`${wrap} pt-6`}>
                    <nav className="hidden md:flex items-center gap-1.5 text-sm font-medium text-grey40 dark:text-nightMuted overflow-x-auto whitespace-nowrap [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                        <Link to="/" className="transition-colors hover:text-grey10 dark:hover:text-white">Home</Link>
                        {event?.data?.address?.country && (
                            <>
                                <ChevronRight className="h-3.5 w-3.5 shrink-0 text-grey70 dark:text-white/30" />
                                <Link
                                    to={`/search?location=${encodeURIComponent(event.data.address.country)}`}
                                    className="transition-colors hover:text-grey10 dark:hover:text-white"
                                >
                                    Events in {event.data.address.country}
                                </Link>
                            </>
                        )}
                        {event?.data?.category && (
                            <>
                                <ChevronRight className="h-3.5 w-3.5 shrink-0 text-grey70 dark:text-white/30" />
                                <Link
                                    to={`/search?category=${encodeURIComponent(event.data.category)}${event?.data?.address?.country ? `&location=${encodeURIComponent(event.data.address.country)}` : ''}`}
                                    className="transition-colors hover:text-grey10 dark:hover:text-white capitalize"
                                >
                                    {event.data.category}{event?.data?.address?.country ? ` in ${event.data.address.country}` : ''}
                                </Link>
                            </>
                        )}
                        <ChevronRight className="h-3.5 w-3.5 shrink-0 text-grey70 dark:text-white/30" />
                        <span className="text-grey10 dark:text-white truncate">{event?.data?.title}</span>
                    </nav>
                    <nav className="flex md:hidden items-center gap-1.5 text-sm font-medium text-grey40 dark:text-nightMuted overflow-x-auto whitespace-nowrap [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                        {event?.data?.address?.country && (
                            <>
                                <Link
                                    to={`/search?location=${encodeURIComponent(event.data.address.country)}`}
                                    className="transition-colors hover:text-grey10 dark:hover:text-white"
                                >
                                    {event.data.address.country}
                                </Link>
                                <ChevronRight className="h-3.5 w-3.5 shrink-0 text-grey70 dark:text-white/30" />
                            </>
                        )}
                        <span className="text-grey10 dark:text-white truncate">{event?.data?.title}</span>
                    </nav>
                </div>
                <div className={`${wrap} pt-4`}>
                    <BannerCarousel
                        event={event?.data}
                    />
                </div>
            </section>
            <div className={`${wrap} pt-8 sm:pt-10 pb-32 md:pb-16 flex flex-col gap-6`}>

                {/* Main Content */}
                <div className="flex flex-col md:flex-row gap-10 lg:gap-16">

                    {/* Left: Main Info */}
                    <div className="flex-1 min-w-0">
                        <div className="mb-12 flex flex-col gap-6">
                            <div className="flex items-start gap-4">
                                {event?.data?.times?.[0]?.startDate && (
                                    <div className="flex w-14 shrink-0 flex-col items-center rounded-2xl bg-white px-2 py-2 text-center text-[#0a0a0a] shadow-lg ring-1 ring-black/[0.06] dark:ring-white/10 sm:w-16">
                                        <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-primary">{moment(event.data.times[0].startDate).format('ddd')}</span>
                                        <span className="text-2xl font-bold leading-none tracking-[-0.04em] sm:text-[28px]">{moment(event.data.times[0].startDate).format('D')}</span>
                                        <span className="mt-0.5 text-[10px] font-bold uppercase tracking-[0.12em] text-grey40">{moment(event.data.times[0].startDate).format('MMM')}</span>
                                    </div>
                                )}
                                <h1 className="min-w-0 break-words text-[clamp(32px,5vw,60px)] font-bold leading-[0.95] tracking-[-0.045em] text-grey10 dark:text-white">{event?.data?.title}</h1>
                            </div>
                            <div className="grid gap-3 sm:grid-cols-2">
                                {(event?.data?.info?.orgName || event?.data?.info?.firstName) && (
                                    <div className={infoRow}>
                                        <span className={infoIcon}><UserRound className="h-5 w-5" /></span>
                                        {event?.data?.info?.orgName ? (
                                            <Link to={`/organization/${event?.data?.info._id}`} className="min-w-0 truncate text-[15px] font-bold text-grey10 underline-offset-4 hover:underline dark:text-white">{event?.data?.info?.orgName}</Link>
                                        ) : (
                                            <Link to={`/user/${event?.data?.info.id}`} className="min-w-0 truncate text-[15px] font-bold text-grey10 underline-offset-4 hover:underline dark:text-white">{event?.data?.info?.firstName}</Link>
                                        )}
                                    </div>
                                )}
                                {event?.data?.times?.[0] && (
                                    <div className={infoRow}>
                                        <span className={infoIcon}><CalendarDays className="h-5 w-5" /></span>
                                        <span className="min-w-0 text-[15px] font-bold text-grey10 dark:text-white">
                                            {moment(event.data.times[0].startDate).format('ddd D MMM')}
                                            <span className='text-grey40 dark:text-nightMuted text-[15px] font-medium'> from </span>
                                            {moment(event.data.times[0].startDate).format('h:mm A')}
                                            {event.data.times[0].endDate && ` - ${moment(event.data.times[0].endDate).format('h:mm A')}`}
                                        </span>
                                    </div>
                                )}
                                {(event?.data?.venue || event?.data?.address?.fullAddress) && (
                                    <div className={infoRow}>
                                        <span className={infoIcon}><Building2 className="h-5 w-5" /></span>
                                        <span className="min-w-0 text-[15px] font-bold text-grey10 dark:text-white">{event?.data?.venue || (event?.data?.showOnlyCity === 1 ? event?.data?.address?.city : event?.data?.address?.fullAddress)}</span>
                                    </div>
                                )}
                                {(event?.data?.venue || event?.data?.address?.fullAddress) && (
                                    <div className={infoRow}>
                                        <span className={infoIcon}><MapPin className="h-5 w-5" /></span>
                                        <span className="min-w-0 text-[15px] font-medium text-grey30 dark:text-nightTextSecondary">{(event?.data?.showOnlyCity === 1 ? event?.data?.address?.city : event?.data?.address?.fullAddress)}</span>
                                    </div>
                                )}
                            </div>
                            <div className="flex items-center gap-3 flex-wrap">
                                {minTicketPrice !== null && (
                                    <button
                                        onClick={() => document.getElementById('tickets')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                                        className={`${btnAccent} cursor-pointer text-[15px]`}
                                    >
                                        <Ticket className="h-4 w-4" />
                                        {minTicketPrice === 0 ? 'Free' : (
                                            <>
                                                Tickets Now At <span className="font-bold">{cur}{minTicketPrice}</span>
                                            </>
                                        )}
                                    </button>
                                )}
                                <LikeButton
                                    eventId={event?.data?._id || event?.data?.id || ''}
                                    initialLikes={event?.data?.likes || 0}
                                    likeStats={event?.data?.likeStats}
                                    className="!h-12 !rounded-full !px-5 !text-sm !font-semibold !shadow-none !bg-transparent text-grey10 ring-1 ring-grey90 hover:!bg-grey99 dark:text-white dark:ring-white/15 dark:hover:!bg-white/[0.06]"
                                    refetchQueries={['event', 'relatedEvents']}
                                    onLikeChange={handleLikeChange}
                                />
                                <button
                                    onClick={() => setShowShareModal(true)}
                                    className="text-grey10 dark:text-white h-12 w-12 flex items-center justify-center rounded-full ring-1 ring-grey90 dark:ring-white/15 transition-colors hover:bg-grey99 dark:hover:bg-white/[0.06]"
                                    aria-label="Share"
                                >
                                    <Share2 className="h-5 w-5" />
                                </button>
                            </div>
                        </div>
                        {/* Tickets Section */}
                        {!event?.data?.venueId && tickets.length > 0 && (
                            <div id="tickets" className="mb-12 scroll-mt-24">
                                <div className="flex items-end justify-between gap-3 mb-5">
                                    <div className={sectionHeading}>Tickets</div>
                                    <span className="flex h-8 items-center rounded-full bg-grey99 px-3 text-[11px] font-bold uppercase tracking-[0.1em] text-grey40 dark:bg-white/[0.06] dark:text-nightMuted">Prices in {curCode}</span>
                                </div>
                                {isEventPassed ? (
                                    <div className="rounded-[24px] bg-red-50 p-5 ring-1 ring-red-200 dark:bg-red-950/30 dark:ring-red-900">
                                        <div className="text-red-800 dark:text-red-400 font-semibold">Event Has Ended</div>
                                    </div>
                                ) : (
                                    <div className="flex flex-col gap-3">
                                        {tickets.map((ticket: any, i: number) => {
                                            const ticketId = ticket._id || i;
                                            const count = ticketCounts[ticketId] || 0;
                                            const soldOut = ticket.remained === 0;
                                            const now = new Date();
                                            const saleStartDate = ticket.saleStart ? new Date(ticket.saleStart) : null;
                                            const saleEndDate = ticket.saleEnd ? new Date(ticket.saleEnd) : null;
                                            const isSaleNotStarted = Boolean(saleStartDate && now < saleStartDate);
                                            const isSaleEnded = Boolean(saleEndDate && now > saleEndDate);
                                            const isTicketInactive = ticket.status === false;
                                            const isUnavailable = soldOut || isSaleNotStarted || isSaleEnded || isTicketInactive;
                                            const unavailableLabel = soldOut
                                                ? 'Sold Out'
                                                : isSaleNotStarted
                                                    ? `Sales Open: ${moment(saleStartDate).format('MMM D')}`
                                                    : isSaleEnded
                                                        ? 'Sale Ended'
                                                        : 'Unavailable';
                                            return (
                                                <div key={ticketId} className={`${card} p-5 flex items-center justify-between gap-4 transition-shadow ${count > 0 ? '!ring-2 !ring-grey10 dark:!ring-nightAccent' : ''}`}>
                                                    <div className="flex-1 flex flex-col gap-2 min-w-0">
                                                        <div className="flex flex-col gap-0.5">
                                                            <div className="text-[17px] font-bold tracking-[-0.02em] text-grey10 dark:text-white">{ticket.name}</div>
                                                            {ticket.description && <div className="text-[14px] text-grey40 dark:text-nightMuted">{ticket.description}</div>}
                                                        </div>
                                                        <div className="text-[20px] font-bold tracking-[-0.03em] text-primary dark:text-nightAccent">{Number(ticket.price) === 0 ? `${cur}0` : `${cur}${ticket.price}`}</div>
                                                    </div>
                                                    {isUnavailable ? (
                                                        <span className="flex h-9 items-center rounded-full bg-grey99 px-4 text-[11px] font-bold uppercase tracking-[0.1em] text-grey40 dark:bg-white/[0.06] dark:text-nightMuted shrink-0 whitespace-nowrap">
                                                            {unavailableLabel}
                                                        </span>
                                                    ) : (
                                                        <div className={stepper}>
                                                            <button
                                                                onClick={() => setTicketCounts(prev => ({ ...prev, [ticketId]: Math.max(0, (prev[ticketId] || 0) - 1) }))}
                                                                className={`${stepBtn} ${count > 0 ? stepBtnOn : stepBtnOff}`}
                                                                disabled={count === 0}
                                                            >
                                                                <Minus className="h-4 w-4" strokeWidth={2.5} />
                                                            </button>
                                                            <span className="min-w-[28px] flex-1 text-center text-[15px] font-bold text-grey10 dark:text-white">{count}</span>
                                                            <button
                                                                onClick={() => setTicketCounts(prev => ({ ...prev, [ticketId]: (prev[ticketId] || 0) + 1 }))}
                                                                className={`${stepBtn} ${stepBtnOn}`}
                                                                disabled={typeof ticket.remained === 'number' && count >= ticket.remained}
                                                            >
                                                                <Plus className="h-4 w-4" strokeWidth={2.5} />
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Venue Seat Map */}
                        {event?.data?.venueId && (
                            <div id="tickets" className="mb-12 scroll-mt-24">
                                <SeatMapSelector
                                    eventId={event?.data?._id || ''}
                                    venueId={event?.data?.venueId || ''}
                                    onSeatSelect={handleSeatSelect}
                                    onSeatDeselect={handleSeatDeselect}
                                    selectedSeats={selectedSeats}
                                    seatMapType="client"
                                    tickets={tickets || []}
                                    currency={event?.data?.currency}
                                    onSeatAssignmentsLoaded={handleSeatAssignmentsLoaded}
                                    onUnresolvedChange={setUnresolvedSeats}
                                    seatTicketChoice={seatTicketChoice}
                                    onSeatTicketChoice={handleSeatTicketChoice}
                                    onSeatChoicesChange={setSeatChoices}
                                />
                            </div>
                        )}
                        {/* About - shown before Location per Figma */}
                        <div id="about" className="mb-12 scroll-mt-24">
                            <div className={`${sectionHeading} mb-5`}>About</div>
                            <div className={`font-vazirmatn text-[16px] leading-relaxed text-grey30 dark:text-nightTextSecondary whitespace-pre-line ${showFullAbout ? '' : 'line-clamp-6'}`}>{renderTextWithLinks(event?.data?.description)}</div>
                            {event?.data?.description && event.data.description.length > 260 && (
                                <button
                                    type="button"
                                    onClick={() => setShowFullAbout(v => !v)}
                                    className={`${btnGhost} mt-5 !h-10 !px-5`}
                                >
                                    {showFullAbout ? 'Read Less' : 'Read More'}
                                </button>
                            )}
                        </div>

                        <div id="location" className="mb-12 scroll-mt-24">
                            <div className={`${sectionHeading} mb-5`}>Location</div>
                            <div className="text-[17px] font-bold tracking-[-0.02em] text-grey10 dark:text-white mb-1 flex items-center gap-2">
                                <MapPin className="h-5 w-5 shrink-0 text-primary dark:text-nightAccent" />

                                {event?.data?.venue}</div>
                            <div className="text-[15px] text-grey40 dark:text-nightMuted mb-4">
                                {event?.data?.showOnlyCity === 1
                                    ? event?.data?.address?.city || event?.data?.address?.fullAddress
                                    : event?.data?.address?.fullAddress
                                }
                            </div>
                            {event?.data?.lat && event?.data?.lng ? (
                                <div className="overflow-hidden rounded-[28px] ring-1 ring-black/[0.06] dark:ring-white/10 [&_img]:!rounded-none [&_img]:!border-0">
                                    <EventMap
                                        lat={event.data.lat}
                                        lng={event.data.lng}
                                        title={event.data.title}
                                        address={event.data.address}
                                    />
                                </div>
                            ) : (
                                <></>
                            )}
                        </div>
                        {event?.data?.howto &&
                            <div id="how-to-get-there" className="mb-12 scroll-mt-24">
                                <div className={`${sectionHeading} mb-5`}>How to get there & Parking</div>
                                <div className={`font-vazirmatn text-[16px] leading-relaxed text-grey30 dark:text-nightTextSecondary whitespace-pre-line ${surface} p-6`}>{renderTextWithLinks(event?.data?.howto)}</div>
                            </div>
                        }
                        {event?.data?.refundPolicy &&
                            <div id="refund-policy" className="mb-12 scroll-mt-24">
                                <div className={`${sectionHeading} mb-5`}>Refund policy</div>
                                <div className={`text-[16px] leading-relaxed text-grey30 dark:text-nightTextSecondary ${surface} p-6`}>{event?.data?.refundPolicy}</div>
                            </div>
                        }
                        {event?.data?.faqs && event?.data?.faqs.length > 0 && (
                            <div id="faq" className="mb-12 scroll-mt-24">
                                <div className={`${sectionHeading} mb-5`}>FAQ</div>
                                <div className="space-y-3">
                                    {event.data.faqs.map((faq: any, index: number) => (
                                        <div key={index} className={`${surface} overflow-hidden !rounded-[24px]`}>
                                            <button
                                                onClick={() => toggleFaq(index)}
                                                className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left transition-colors duration-200 hover:bg-black/[0.02] dark:hover:bg-white/[0.04]"
                                            >
                                                <div className="font-bold tracking-[-0.01em] text-grey10 dark:text-white text-[16px]">{faq.question}</div>
                                                <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-white ring-1 ring-black/[0.06] dark:bg-white/[0.06] dark:ring-white/10">
                                                    <ChevronDown className={`h-4 w-4 text-grey30 dark:text-nightTextSecondary transition-transform duration-200 ${expandedFaqs[index] ? 'rotate-180' : ''}`} />
                                                </span>
                                            </button>
                                            {expandedFaqs[index] && (
                                                <div className="px-5 pb-5">
                                                    <div className="text-grey30 dark:text-nightTextSecondary text-[15px] leading-relaxed">{renderTextWithLinks(faq.answer)}</div>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        <EventCommentsSection eventId={event?.data?._id} commentsDisabled={event?.data?.commentsDisabled === 1} />
                    </div>
                    {/* Right: Sidebar */}
                    <div className="w-full md:w-[380px] lg:w-[400px] flex-shrink-0 flex flex-col gap-6 sticky top-24 self-start">
                        <div className={`hidden md:flex flex-col gap-5 p-6 ${card} shadow-[0_30px_80px_-40px_rgba(0,0,0,0.35)] dark:bg-nightSurface ${event?.data?.venueId && !isEventPassed ? 'max-h-[calc(100dvh-8rem)] overflow-y-auto overscroll-contain' : ''}`}>
                            {isEventPassed ? (
                                <div>
                                    <div className="flex h-12 w-full items-center justify-center rounded-full bg-grey99 font-semibold text-grey40 dark:bg-white/[0.06] dark:text-nightMuted">Event Has Ended</div>
                                </div>
                            ) : event?.data?.venueId ? (
                                seatCheckout
                            ) : (
                                <>
                                    {/* Header */}
                                    <div>
                                        {totalTicketCount > 0 ? (
                                            <>
                                                <div className={checkoutTitle}>x{totalTicketCount}</div>
                                                <div className={checkoutSub}>Selected Items</div>
                                            </>
                                        ) : (
                                            <>
                                                <div className={checkoutTitle}>Select Tickets</div>
                                                {minTicketPrice !== null && (
                                                    <div className={checkoutSub}>{minTicketPrice === 0 ? 'Free' : `From ${cur}${minTicketPrice}`}</div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                    {/* Ticket rows — only show selected, capped to ~2 rows tall then scrolls internally */}
                                    <div className="flex flex-col gap-2 max-h-[164px] overflow-y-auto">
                                        {tickets.map((ticket: any, i: number) => {
                                            const ticketId = ticket._id || i;
                                            const count = ticketCounts[ticketId] || 0;
                                            if (count === 0) return null;
                                            return (
                                                <div key={ticketId} className="rounded-2xl bg-grey99 dark:bg-white/[0.06] px-4 py-3 flex items-center justify-between gap-3 w-full flex-shrink-0">
                                                    <div className="min-w-0 truncate text-[15px] text-grey10 dark:text-white font-semibold">{ticket.name}</div>
                                                    <div className={`${stepper} !h-10`}>
                                                        <button
                                                            onClick={() => setTicketCounts(prev => ({ ...prev, [ticketId]: Math.max(0, (prev[ticketId] || 0) - 1) }))}
                                                            className={`${stepBtn} !h-8 !w-8 ${stepBtnOn}`}
                                                        >
                                                            <Minus className="h-3.5 w-3.5" strokeWidth={2.5} />
                                                        </button>
                                                        <span className="text-[14px] font-bold text-grey10 dark:text-white min-w-[24px] text-center">{count}</span>
                                                        <button
                                                            onClick={() => setTicketCounts(prev => ({ ...prev, [ticketId]: (prev[ticketId] || 0) + 1 }))}
                                                            className={`${stepBtn} !h-8 !w-8 ${stepBtnOn}`}
                                                        >
                                                            <Plus className="h-3.5 w-3.5" strokeWidth={2.5} />
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    {/* Promo code */}
                                    <div className="w-full">
                                        <button
                                            onClick={() => setShowPromoInput(p => !p)}
                                            className="w-full h-12 rounded-full flex items-center justify-center gap-2 text-grey10 ring-1 ring-grey90 transition-colors hover:bg-grey99 dark:text-white dark:ring-white/15 dark:hover:bg-white/[0.06]"
                                        >
                                            <Gift className="h-5 w-5" />

                                            <span className="text-[15px] font-semibold capitalize">Add Promo Code</span>
                                        </button>
                                        {showPromoInput && (
                                            <form
                                                className="mt-3 flex items-center gap-2"
                                                onSubmit={e => { e.preventDefault(); handleApplyPromo(); }}
                                            >
                                                <Input
                                                    type="text"
                                                    className='no-global-input flex-1'
                                                    classNames={{
                                                        inputWrapper: commentInputWrapper,
                                                        input: 'text-[15px] text-grey10 placeholder:text-grey60 dark:text-white dark:placeholder:text-nightMuted',
                                                    }}
                                                    value={promoCode}
                                                    onChange={e => setPromoCode(e.target.value)}
                                                    placeholder="Enter promo code"
                                                />
                                                <button
                                                    type="submit"
                                                    disabled={!promoCode.trim()}
                                                    className="h-12 px-5 rounded-full bg-grey10 text-white text-sm font-semibold shrink-0 transition-colors hover:bg-black disabled:opacity-40 dark:bg-white dark:text-nightBg dark:hover:bg-grey90"
                                                >
                                                    Apply
                                                </button>
                                            </form>
                                        )}
                                    </div>
                                    {/* Total */}
                                    {totalTicketCount > 0 && (
                                        <div className="w-full border-t border-black/[0.06] pt-4 dark:border-white/10">
                                            <div className="flex items-center justify-between text-[22px] font-bold tracking-[-0.03em] text-grey10 dark:text-white leading-8">
                                                <span>Total:</span>
                                                <span>{cur}{(totalPrice || 0).toFixed(2)}</span>
                                            </div>
                                            <div className="flex items-center gap-1.5 text-[13px] text-grey40 dark:text-nightMuted">
                                                <span>Includes Fees</span>
                                                <Info className="h-4 w-4" />
                                            </div>
                                        </div>
                                    )}
                                    {/* Book / Get Tickets */}
                                    <div className="w-full">
                                        {isSelectedTimePassed ? (
                                            <div className="flex h-12 w-full items-center justify-center rounded-full bg-grey99 text-sm font-semibold text-grey40 dark:bg-white/[0.06] dark:text-nightMuted">Selected Date Has Passed</div>
                                        ) : (
                                            <Button
                                                className="no-global-button-color w-full h-12 rounded-full bg-grey10 text-white text-[15px] font-semibold capitalize transition-colors hover:bg-black dark:bg-white dark:text-nightBg dark:hover:bg-grey90 disabled:opacity-60"
                                                onClick={() => {
                                                    setValidationErrors({});
                                                    if (!selectedTime) {
                                                        setValidationErrors({ date: true });
                                                        setShowCheckoutWarning(true);
                                                        document.getElementById('date-time')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                                        return;
                                                    }
                                                    setShowCheckoutWarning(false);
                                                    setShowGetTicketsModal(true);
                                                }}
                                            >
                                                Book
                                            </Button>
                                        )}
                                        {showCheckoutWarning && !isEventPassed && !isSelectedTimePassed && (
                                            <div className="text-red-500 dark:text-red-400 text-xs mt-2 text-center">Please select a date and time first.</div>
                                        )}
                                        {isSelectedTimePassed && !isEventPassed && (
                                            <div className="text-red-500 dark:text-red-400 text-xs mt-2 text-center">The selected date has passed. Choose a future date.</div>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>

                    </div>
                </div>
                {/* Keep Exploring */}
                {
                    relatedEvents.length > 0 && (
                        <section className="mt-16 sm:mt-24">
                            <h2 className="text-[clamp(36px,5.5vw,72px)] font-bold leading-[0.95] tracking-[-0.045em] text-grey10 dark:text-white">Keep Exploring</h2>
                            <div className="mt-4 grid grid-cols-1 content-start sm:-mx-4 sm:grid-cols-2 lg:grid-cols-3">
                                {relatedEvents.map((rel: any, i: number) => (
                                    <EventCard
                                        key={rel._id || i}
                                        event={rel}
                                        userInfo={undefined}
                                        organizerInfo={rel.organizerId}
                                        onLikeChange={(likes, isLiked) => {
                                            console.log(`Related event ${rel._id} like status changed: ${likes} likes, isLiked: ${isLiked}`);
                                        }}
                                    />
                                ))}
                            </div>
                        </section>
                    )
                }
            </div >

            {/* Sticky Checkout Section - Mobile Only. Mirrors the desktop sidebar. For the
                plain ticket flow it only appears once a ticket has actually been added
                with the +/- stepper — not before, and not as an empty "Select Tickets" prompt. */}
            {showMobileCheckoutBar && (
                < div className={`md:hidden fixed bottom-0 left-0 right-0 z-50 rounded-t-[28px] bg-white ring-1 ring-black/[0.06] shadow-[0_-16px_40px_rgba(0,0,0,0.12)] dark:bg-nightSurface dark:ring-white/10 ${event?.data?.venueId && !isEventPassed ? 'flex flex-col' : 'max-h-[70dvh] overflow-y-auto'}`} style={{ paddingBottom: 'max(4px, env(safe-area-inset-bottom))' }}>
                    {isEventPassed ? (
                        <div className="p-5">
                            <div className="flex h-12 w-full items-center justify-center rounded-full bg-grey99 px-6 font-semibold text-grey40 dark:bg-white/[0.06] dark:text-nightMuted">Event Has Ended</div>
                        </div>
                    ) : event?.data?.venueId ? (
                        <>
                            <div className="shrink-0 flex justify-center pt-2.5"><span className="h-1 w-10 rounded-full bg-grey90 dark:bg-white/20" /></div>
                            {mobileSeatCheckout}
                        </>
                    ) : (
                        <div className="flex flex-col gap-4 pt-3 pb-5">
                            <div className="shrink-0 flex justify-center"><span className="h-1 w-10 rounded-full bg-grey90 dark:bg-white/20" /></div>
                            {/* Header */}
                            <div className="px-5">
                                {totalTicketCount > 0 ? (
                                    <>
                                        <div className={checkoutTitle}>x{totalTicketCount}</div>
                                        <div className={checkoutSub}>Selected Items</div>
                                    </>
                                ) : (
                                    <>
                                        <div className={checkoutTitle}>Select Tickets</div>
                                        {minTicketPrice !== null && (
                                            <div className={checkoutSub}>{minTicketPrice === 0 ? 'Free' : `From ${cur}${minTicketPrice}`}</div>
                                        )}
                                    </>
                                )}
                            </div>
                            {/* Ticket rows — only show selected, capped to ~2 rows tall then scrolls internally */}
                            <div className="flex flex-col gap-2 max-h-[164px] overflow-y-auto px-5">
                                {tickets.map((ticket: any, i: number) => {
                                    const ticketId = ticket._id || i;
                                    const count = ticketCounts[ticketId] || 0;
                                    if (count === 0) return null;
                                    return (
                                        <div key={ticketId} className="rounded-2xl bg-grey99 dark:bg-white/[0.06] px-4 py-3 flex items-center justify-between gap-3 w-full flex-shrink-0">
                                            <div className="min-w-0 truncate text-[15px] text-grey10 dark:text-white font-semibold">{ticket.name}</div>
                                            <div className={`${stepper} !h-10`}>
                                                <button
                                                    onClick={() => setTicketCounts(prev => ({ ...prev, [ticketId]: Math.max(0, (prev[ticketId] || 0) - 1) }))}
                                                    className={`${stepBtn} !h-8 !w-8 ${stepBtnOn}`}
                                                >
                                                    <Minus className="h-3.5 w-3.5" strokeWidth={2.5} />
                                                </button>
                                                <span className="text-[14px] font-bold text-grey10 dark:text-white min-w-[24px] text-center">{count}</span>
                                                <button
                                                    onClick={() => setTicketCounts(prev => ({ ...prev, [ticketId]: (prev[ticketId] || 0) + 1 }))}
                                                    className={`${stepBtn} !h-8 !w-8 ${stepBtnOn}`}
                                                >
                                                    <Plus className="h-3.5 w-3.5" strokeWidth={2.5} />
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            {/* Promo code */}
                            <div className="px-5 w-full">
                                <button
                                    onClick={() => setShowPromoInput(p => !p)}
                                    className="w-full h-12 rounded-full flex items-center justify-center gap-2 text-grey10 ring-1 ring-grey90 transition-colors hover:bg-grey99 dark:text-white dark:ring-white/15 dark:hover:bg-white/[0.06]"
                                >
                                    <Gift className="h-5 w-5" />

                                    <span className="text-[15px] font-semibold capitalize">Add Promo Code</span>
                                </button>
                                {showPromoInput && (
                                    <form
                                        className="mt-3 flex items-center gap-2"
                                        onSubmit={e => { e.preventDefault(); handleApplyPromo(); }}
                                    >
                                        <input
                                            type="text"
                                            value={promoCode}
                                            onChange={e => setPromoCode(e.target.value)}
                                            placeholder="Enter promo code"
                                            className={`${input} flex-1 min-w-0`}
                                        />
                                        <button
                                            type="submit"
                                            disabled={!promoCode.trim()}
                                            className="h-12 px-5 rounded-full bg-grey10 text-white text-sm font-semibold shrink-0 transition-colors hover:bg-black disabled:opacity-40 dark:bg-white dark:text-nightBg dark:hover:bg-grey90"
                                        >
                                            Apply
                                        </button>
                                    </form>
                                )}
                            </div>
                            {/* Total */}
                            {totalTicketCount > 0 && (
                                <div className="w-full px-5">
                                    <div className="flex items-center justify-between text-[22px] font-bold tracking-[-0.03em] text-grey10 dark:text-white leading-8">
                                        <span>Total:</span>
                                        <span>{cur}{(totalPrice || 0).toFixed(2)}</span>
                                    </div>
                                    <div className="flex items-center gap-1.5 text-[13px] text-grey40 dark:text-nightMuted">
                                        <span>Includes Fees</span>
                                        <Info className="h-4 w-4" />
                                    </div>
                                </div>
                            )}
                            {/* Book */}
                            <div className="px-5 w-full sticky bottom-0 bg-white dark:bg-nightSurface pt-2 -mb-5 pb-5">
                                {isSelectedTimePassed ? (
                                    <div className="flex h-12 w-full items-center justify-center rounded-full bg-grey99 text-sm font-semibold text-grey40 dark:bg-white/[0.06] dark:text-nightMuted">Selected Date Has Passed</div>
                                ) : (
                                    <Button
                                        className="no-global-button-color w-full h-12 rounded-full bg-grey10 text-white text-[15px] font-semibold capitalize transition-colors hover:bg-black dark:bg-white dark:text-nightBg dark:hover:bg-grey90 disabled:opacity-60"
                                        onClick={() => {
                                            setValidationErrors({});
                                            if (!selectedTime) {
                                                setValidationErrors({ date: true });
                                                setShowCheckoutWarning(true);
                                                document.getElementById('date-time')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                                return;
                                            }
                                            setShowCheckoutWarning(false);
                                            setShowGetTicketsModal(true);
                                        }}
                                    >
                                        Book
                                    </Button>
                                )}
                                {showCheckoutWarning && !isEventPassed && !isSelectedTimePassed && (
                                    <div className="text-red-500 dark:text-red-400 text-xs mt-2 text-center">Please select a date and time first.</div>
                                )}
                                {isSelectedTimePassed && !isEventPassed && (
                                    <div className="text-red-500 dark:text-red-400 text-xs mt-2 text-center">The selected date has already passed. Please choose a future date.</div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}



            {/* Floating Action Buttons */}
            <div className={`fixed bottom-6 right-6 z-50 flex-col gap-3 ${showMobileCheckoutBar ? 'hidden md:flex' : 'flex'}`}>
                {/* Share Button */}
                {/* <button
                    onClick={() => setShowShareModal(true)}
                    className="bg-primary50 text-white rounded-full w-12 h-12 flex items-center justify-center shadow-lg hover:bg-primary60 transition-all duration-300 hover:scale-110"
                    aria-label="Share event"
                >
                    <Share width={20} height={20} />
                </button> */}

                {/* Back to Top Button */}
                {showBackToTop && (
                    <button
                        onClick={() => {
                            window.scrollTo({
                                top: 0,
                                behavior: 'smooth'
                            });
                        }}
                        className="bg-grey10 text-white dark:bg-white dark:text-nightBg rounded-full w-12 h-12 flex items-center justify-center shadow-lg ring-1 ring-black/[0.06] dark:ring-white/10 hover:bg-black dark:hover:bg-grey90 transition-all duration-300 hover:scale-110"
                        aria-label="Back to top"
                    >
                        <ArrowUp className="h-5 w-5" />
                    </button>
                )}
            </div>

            <Footer hasMobileNav />
            {!showMobileCheckoutBar && <MobileBottomNav />}

            {/* Share Modal */}
            <ShareModal
                isOpen={showShareModal}
                onClose={() => setShowShareModal(false)}
                eventTitle={event?.data?.title || 'Event'}
                eventUrl={event?.data?.handle ? getBaseUrl() + `/e/${event?.data?.handle}` : getBaseUrl() + location.pathname}
                eventImage={event?.data?.attachments?.banner?.link}
                eventDescription={event?.data?.description}
            />

            {/* Report Event Modal */}
            <ReportEventModal
                isOpen={showReportModal}
                onClose={() => setShowReportModal(false)}
                onSubmit={reportEventMutation.mutate}
                isSubmitting={reportEventMutation.isPending}
            />

            {/* Get Tickets Modal */}
            <GetTicketsModal
                isOpen={showGetTicketsModal}
                onClose={() => setShowGetTicketsModal(false)}
                event={event?.data}
                tickets={tickets}
                selectedTime={selectedTime}
                selectedDateForApi={formatSelectedDate(selectedTime)}
                initialTicketCounts={ticketCounts}
                initialPromoCode={promoCode}
                selectedSeats={modalSeats}
            />
        </div >
    );
} 